import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Shield, Trash2, Search, UserPlus } from 'lucide-react';
import { usersApi, SystemAdmin } from '../lib/api';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { LoadingSpinner } from '../components/LoadingScreen';
import { useTelegram } from '../hooks/useTelegram';

export function SystemAdminsPage() {
  const { hapticFeedback, showAlert, showConfirm } = useTelegram();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Получаем текущего пользователя для проверки прав
  const { data: currentUser } = useQuery({
    queryKey: ['user', 'me'],
    queryFn: () => usersApi.me(),
  });

  const isSystemAdmin = currentUser?.isSystemAdmin ?? false;

  // Получаем список системных админов
  const { data: systemAdmins, isLoading } = useQuery({
    queryKey: ['systemAdmins'],
    queryFn: () => usersApi.getSystemAdmins(),
    enabled: isSystemAdmin,
  });

  // Поиск пользователей для добавления
  const { data: searchResults } = useQuery({
    queryKey: ['users', 'search', searchQuery],
    queryFn: () => usersApi.search(searchQuery),
    enabled: searchQuery.length >= 2 && showAddModal,
  });

  const assignAdminMutation = useMutation({
    mutationFn: (userId: string) => usersApi.assignSystemAdmin(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemAdmins'] });
      hapticFeedback('success');
      showAlert('Системный администратор назначен!');
      setShowAddModal(false);
      setSearchQuery('');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const removeAdminMutation = useMutation({
    mutationFn: (userId: string) => usersApi.removeSystemAdmin(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemAdmins'] });
      hapticFeedback('success');
      showAlert('Системный администратор снят!');
    },
    onError: (err: Error) => {
      hapticFeedback('error');
      showAlert(`Ошибка: ${err.message}`);
    },
  });

  const handleRemoveAdmin = async (admin: SystemAdmin) => {
    hapticFeedback('light');
    const confirmed = await showConfirm(
      `Снять системного администратора ${admin.firstName} ${admin.lastName || ''}?`
    );
    if (confirmed) {
      removeAdminMutation.mutate(admin.id);
    }
  };

  const handleAddAdmin = (userId: string) => {
    hapticFeedback('light');
    assignAdminMutation.mutate(userId);
  };

  if (!isSystemAdmin) {
    return (
      <div className="p-4 text-center">
        <Shield className="w-12 h-12 text-tg-hint mx-auto mb-4" />
        <p className="text-tg-text font-medium mb-2">Доступ запрещён</p>
        <p className="text-sm text-tg-hint">
          Только системные администраторы могут управлять другими системными администраторами.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Заголовок */}
      <div className="flex items-center gap-2">
        <Shield className="w-6 h-6 text-tg-button" />
        <h1 className="text-xl font-bold text-tg-text">Системные администраторы</h1>
      </div>

      <Card className="p-4">
        <p className="text-sm text-tg-hint mb-4">
          Системные администраторы могут создавать проекты, добавлять людей в проекты и управлять всеми проектами в системе.
        </p>

        <Button
          onClick={() => {
            hapticFeedback('light');
            setShowAddModal(true);
          }}
          className="w-full gap-2"
        >
          <UserPlus className="w-4 h-4" />
          Добавить системного администратора
        </Button>
      </Card>

      {/* Модальное окно добавления */}
      {showAddModal && (
        <Card className="p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-tg-text">Добавить системного администратора</h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setShowAddModal(false);
                setSearchQuery('');
              }}
            >
              ✕
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Поиск пользователя
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Введите имя, фамилию или @username"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          {searchQuery.length >= 2 && searchResults && (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {searchResults.length === 0 ? (
                <p className="text-sm text-tg-hint text-center py-4">
                  Пользователи не найдены
                </p>
              ) : (
                searchResults
                  .filter((user) => !systemAdmins?.some((admin) => admin.id === user.id))
                  .map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center justify-between p-3 bg-tg-bg rounded-lg"
                    >
                      <div>
                        <p className="font-medium text-tg-text">
                          {user.lastName || ''} {user.firstName}
                        </p>
                        {user.username && (
                          <p className="text-sm text-tg-hint">@{user.username}</p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAddAdmin(user.id)}
                        isLoading={assignAdminMutation.isPending}
                      >
                        Добавить
                      </Button>
                    </div>
                  ))
              )}
            </div>
          )}
        </Card>
      )}

      {/* Список системных админов */}
      <Card>
        <div className="divide-y divide-tg-bg">
          {systemAdmins && systemAdmins.length === 0 ? (
            <div className="p-4 text-center text-tg-hint">
              Нет системных администраторов
            </div>
          ) : (
            systemAdmins?.map((admin) => (
              <div key={admin.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-yellow-500" />
                      <p className="font-medium text-tg-text">
                        {admin.lastName || ''} {admin.firstName}
                      </p>
                    </div>
                    {admin.username && (
                      <p className="text-sm text-tg-hint">@{admin.username}</p>
                    )}
                    {admin.id === currentUser?.id && (
                      <Badge variant="info" className="mt-2">
                        Вы
                      </Badge>
                    )}
                  </div>
                  {admin.id !== currentUser?.id && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveAdmin(admin)}
                      disabled={removeAdminMutation.isPending}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </Card>
    </div>
  );
}

